import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ClerkProvider } from '@clerk/clerk-react';
import App from './App';
import './index.css';
import './styles/badges.css';
import { GeolocationServiceProvider } from './contexts/GeolocationServiceContext';
import { VideoServiceProvider } from './contexts/VideoServiceContext';
import { ModalProvider } from './services/ModalService';
import { UnitsProvider } from './contexts/UnitsContext';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import analytics from './services/AnalyticsService';

// Tag <body> when we're running inside the Capacitor native wrapper so CSS
// can apply native-only adjustments (status-bar safe-area floors, etc.).
if (typeof window !== 'undefined' && window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform()) {
  document.body.classList.add('native-app');
}

// HTML5 drag-and-drop backend can only be mounted once per window. Decide
// the backend at module load (it can't change at runtime without remounting
// every drag source / target) and use a single global DndProvider below.
// IMPORTANT: this provider must wrap ModalProvider so that drag sources/drops
// rendered inside modals (e.g. Workout shown by StrategyModal/PlanEditor)
// have a DnD context — modals are portaled as siblings of <App>.
const _isTouchPrimary = (typeof navigator !== 'undefined') && (
  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
  (navigator.maxTouchPoints && navigator.maxTouchPoints > 0 &&
    window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
);
const DND_BACKEND = _isTouchPrimary ? TouchBackend : HTML5Backend;
// TouchBackend listens to touch events globally. Without these options it
// would `preventDefault()` on every touchmove and the page becomes
// unscrollable. NOTE: enableMouseEvents intentionally NOT set — with it on,
// react-dnd-touch-backend@16 leaves internal mouse state behind after a touch-
// driven drag and silently refuses to start subsequent drags.
const DND_BACKEND_OPTIONS = _isTouchPrimary
  ? { delayTouchStart: 0, touchSlop: 8, ignoreContextMenu: true }
  : undefined;

// Initialize analytics
analytics.init();

// Enforce HTTPS when not running locally
if (process.env.REACT_APP_ENV === 'production' && window.location.protocol !== 'https:') {
  window.location.href = `https://${window.location.hostname}${window.location.pathname}${window.location.search}`;
}

// Clerk Publishable Key
const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing REACT_APP_CLERK_PUBLISHABLE_KEY environment variable');
}

// Defaults are deliberately quieter than react-query's. Every mutation that
// touches this data calls invalidateQueries, which ignores staleTime, so edits
// still refetch immediately.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false
    }
  }
});
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/">
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <QueryClientProvider client={queryClient}>
        <GeolocationServiceProvider>
          <VideoServiceProvider>
            <UnitsProvider>
              <DndProvider backend={DND_BACKEND} options={DND_BACKEND_OPTIONS}>
                <ModalProvider>
                  <App />
                </ModalProvider>
              </DndProvider>
            </UnitsProvider>
          </VideoServiceProvider>
        </GeolocationServiceProvider>
      </QueryClientProvider>
    </Router>
  </ClerkProvider>
);
